import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { FaLock, FaCheck, FaEye, FaFileDownload } from "react-icons/fa";
import CreditAuctionProposalsPagination from "../CreditAuctionProposalsPage/credit-auction-proposals-pagination";
import {
  EDGE_COLUMN_PX,
  tableCellStyles,
  tableHeaderStyles,
  tagButtonStyle,
} from "../FinancialShared/tableStyles";
import {
  bloquearConsumoCorban,
  ConsumoCotaCorbanItem,
  gerarRelatorioConsumoCorban,
  getConsumoCotasCorbanAtivas,
  getCotasInativasByCorbanId,
  liberarConsumoCorban,
} from "@/services/consignado/consume-quotes-corban.service";
import { useDebounce } from "@/utils/useDebounce";

type ConsumoTableProps = {
  searchTerm?: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

// Converte uma data (ISO/Date do backend) para DD/MM/YYYY,
// formato esperado pelo endpoint de relatório.
const dateToBrDate = (value: string | Date): string => {
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

export default function ConsumoTable({
  searchTerm = "",
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: ConsumoTableProps) {
  const backgroundStyle = useConditionalBackground();
  const toast = useToast();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const [cotas, setCotas] = useState<ConsumoCotaCorbanItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingCotas, setIsLoadingCotas] = useState(false);
  const [isErrorCotas, setIsErrorCotas] = useState(false);
  const [actionCorbanId, setActionCorbanId] = useState<number | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedCota, setSelectedCota] = useState<
    ConsumoCotaCorbanItem | undefined
  >(undefined);
  const [cotasInativas, setCotasInativas] = useState<ConsumoCotaCorbanItem[]>(
    [],
  );
  const [loadingInativas, setLoadingInativas] = useState(false);

  const [generatingReportId, setGeneratingReportId] = useState<string | null>(
    null,
  );

  const fetchCotas = useCallback(async () => {
    setIsLoadingCotas(true);
    setIsErrorCotas(false);
    try {
      const response = await getConsumoCotasCorbanAtivas(
        page,
        pageSize,
        debouncedSearch,
      );
      setCotas(response.data ?? []);
      setTotalItems(response.total ?? 0);
      setTotalPages(Math.max(1, response.totalPages ?? 1));
    } catch (error) {
      console.error("Erro ao buscar consumo de cotas:", error);
      setIsErrorCotas(true);
      setCotas([]);
    } finally {
      setIsLoadingCotas(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchCotas();
  }, [fetchCotas]);

  useEffect(() => {
    if (page > totalPages) {
      onPageChange(1);
    }
  }, [totalPages, page, onPageChange]);

  const handleLiberarConsumo = async (cota: ConsumoCotaCorbanItem) => {
    setActionCorbanId(cota.idCorban);
    try {
      await liberarConsumoCorban(cota.idCorban);
      toast({
        title: `Consumo liberado para corban ${cota.idCorban}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await fetchCotas();
    } catch (error) {
      console.error("Erro ao liberar consumo:", error);
      toast({
        title: "Erro ao liberar consumo",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setActionCorbanId(null);
    }
  };

  const handleBloquearConsumo = async (cota: ConsumoCotaCorbanItem) => {
    setActionCorbanId(cota.idCorban);
    try {
      await bloquearConsumoCorban(cota.idCorban);
      toast({
        title: `Consumo bloqueado para corban ${cota.idCorban}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await fetchCotas();
    } catch (error) {
      console.error("Erro ao bloquear consumo:", error);
      toast({
        title: "Erro ao bloquear consumo",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setActionCorbanId(null);
    }
  };

  const handleVisualizarConsumo = async (cota: ConsumoCotaCorbanItem) => {
    setSelectedCota(cota);
    setLoadingInativas(true);
    setCotasInativas([]);
    onOpen();
    try {
      const inativas = await getCotasInativasByCorbanId(cota.idCorban);
      setCotasInativas(inativas ?? []);
    } catch (error) {
      console.error("Erro ao buscar histórico de consumo:", error);
      toast({
        title: "Erro ao carregar histórico de consumo",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoadingInativas(false);
    }
  };

  const handleGerarRelatorio = async (cota: ConsumoCotaCorbanItem) => {
    // Data início é fixa: a própria data de criação exibida na linha.
    // Data fim não é enviada — o backend sempre considera a data atual.
    setGeneratingReportId(cota.id);
    try {
      const { fileUrl } = await gerarRelatorioConsumoCorban(cota.idCorban, {
        dataInicio: dateToBrDate(cota.createdAt),
        infoConsumo: {
          numeroConsultas: cota.numeroConsultas ?? 0,
          numeroConsultasSucesso: cota.numeroConsultasSucesso ?? 0,
          numeroCcbsPagas: cota.numeroCcbs ?? 0,
          consultasMargem: cota.consultasMargem ?? 0,
        },
      });
      window.open(fileUrl, "_blank", "noopener,noreferrer");
      toast({
        title: `Relatório do corban ${cota.idCorban} gerado com sucesso`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      toast({
        title: "Erro ao gerar relatório",
        description: "Não foi possível gerar o arquivo. Tente novamente.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setGeneratingReportId(null);
    }
  };

  const searchQuery = debouncedSearch.trim();

  return (
    <>
      {isLoadingCotas ? (
        <Flex justify="center" align="center" minH="240px">
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : isErrorCotas ? (
        <Flex direction="column" justify="center" align="center" minH="240px" gap={4}>
          <Text color="red.500">Erro ao carregar consumos.</Text>
          <Button onClick={fetchCotas} colorScheme="teal" size="sm">
            Tentar novamente
          </Button>
        </Flex>
      ) : cotas.length !== 0 ? (
        <Box bg="white" borderRadius="xl" boxShadow="sm" px={0} py={{ base: 2, md: 4 }}>
          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />

          <TableContainer borderRadius="lg" overflowX="auto">
            <Table variant="simple">
              <Thead {...backgroundStyle}>
                <Tr>
                  <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX} w="200px">
                    AÇÕES
                  </Th>
                  <Th {...tableHeaderStyles}>ID CORBAN</Th>
                  <Th {...tableHeaderStyles}>NOME CORBAN</Th>
                  <Th {...tableHeaderStyles}>NOME PROMOTORA</Th>
                  <Th {...tableHeaderStyles}>DATA INÍCIO</Th>
                  <Th {...tableHeaderStyles}>PRODUTO</Th>
                  <Th {...tableHeaderStyles}>CONSULTAS</Th>
                  <Th {...tableHeaderStyles}>CONSULTAS COM MARGEM</Th>
                  <Th {...tableHeaderStyles}>CONSULTAS COM SUCESSO</Th>
                  <Th {...tableHeaderStyles}>TOTAL CCBs PAGAS</Th>
                  <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX}>
                    STATUS
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {cotas.map((cota) => (
                  <Tr key={cota.id}>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      <SimpleGrid columns={2} spacing={1}>
                        <Tooltip label="Liberar Consumo" placement="top">
                          <Button
                            leftIcon={<FaCheck />}
                            size="xs"
                            {...tagButtonStyle("green")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleLiberarConsumo(cota)}
                            isDisabled={actionCorbanId === cota.idCorban}
                            isLoading={actionCorbanId === cota.idCorban}
                          >
                            Liberar
                          </Button>
                        </Tooltip>
                        <Tooltip
                          label={
                            cota.consumoBloqueado
                              ? "Consumo já bloqueado"
                              : "Bloquear Consumo"
                          }
                          placement="top"
                        >
                          <Button
                            leftIcon={<FaLock />}
                            size="xs"
                            {...tagButtonStyle("red")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleBloquearConsumo(cota)}
                            isDisabled={
                              cota.consumoBloqueado ||
                              actionCorbanId === cota.idCorban
                            }
                            isLoading={actionCorbanId === cota.idCorban}
                          >
                            Bloquear
                          </Button>
                        </Tooltip>
                        <Tooltip label="Visualizar Histórico" placement="top">
                          <Button
                            leftIcon={<FaEye />}
                            size="xs"
                            {...tagButtonStyle("blue")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleVisualizarConsumo(cota)}
                          >
                            Histórico
                          </Button>
                        </Tooltip>
                        <Tooltip label="Baixar Relatório" placement="top">
                          <Button
                            leftIcon={<FaFileDownload />}
                            size="xs"
                            {...tagButtonStyle("teal")}
                            fontFamily="Lexend Deca"
                            fontWeight="normal"
                            px={2}
                            onClick={() => handleGerarRelatorio(cota)}
                            isDisabled={generatingReportId === cota.id}
                            isLoading={generatingReportId === cota.id}
                          >
                            Relatório
                          </Button>
                        </Tooltip>
                      </SimpleGrid>
                    </Td>
                    <Td {...tableCellStyles} color="blue.500">
                      {cota.idCorban}
                    </Td>
                    <Td {...tableCellStyles}>{cota.nomeCorban ?? "-"}</Td>
                    <Td {...tableCellStyles}>{cota.nomePromotora ?? "-"}</Td>
                    <Td {...tableCellStyles}>
                      {cota.createdAt
                        ? new Date(cota.createdAt).toLocaleDateString("pt-BR")
                        : "-"}
                    </Td>
                    <Td {...tableCellStyles}>CLT</Td>
                    <Td {...tableCellStyles}>{cota.numeroConsultas ?? 0}</Td>
                    <Td {...tableCellStyles}>{cota.consultasMargem ?? 0}</Td>
                    <Td {...tableCellStyles}>
                      {cota.numeroConsultasSucesso ?? 0}
                    </Td>
                    <Td {...tableCellStyles}>{cota.numeroCcbs ?? 0}</Td>
                    <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                      <Badge
                        colorScheme={cota.consumoBloqueado ? "red" : "green"}
                        borderRadius="md"
                        px={2}
                        py={1}
                        fontFamily="Lexend Deca"
                        fontSize="xs"
                        fontWeight="normal"
                      >
                        {cota.consumoBloqueado ? "Bloqueada" : "Ativa"}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          <CreditAuctionProposalsPagination
            currentPage={page}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Box>
      ) : (
        <Heading mt={8} textAlign="center" color="gray.600">
          {searchQuery
            ? "Nenhum corban encontrado para a busca informada"
            : "Não há corbans para exibir"}
        </Heading>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="1100px" maxH="90vh">
          <ModalHeader fontFamily="Lexend Deca">
            Consumos - Corban {selectedCota?.idCorban}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" maxH="calc(90vh - 140px)">
            {loadingInativas ? (
              <Flex justify="center" align="center" minH="200px">
                <Spinner size="xl" color="teal.500" />
              </Flex>
            ) : cotasInativas.length > 0 ? (
              <TableContainer borderRadius="lg" overflowX="auto">
                <Table variant="simple">
                  <Thead {...backgroundStyle}>
                    <Tr>
                      <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX}>
                        DATA INÍCIO
                      </Th>
                      <Th {...tableHeaderStyles}>CONSULTAS</Th>
                      <Th {...tableHeaderStyles}>CONSULTAS COM MARGEM</Th>
                      <Th {...tableHeaderStyles}>CONSULTAS COM SUCESSO</Th>
                      <Th {...tableHeaderStyles}>TOTAL CCBs PAGAS</Th>
                      <Th {...tableHeaderStyles} px={EDGE_COLUMN_PX}>
                        DATA FIM
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cotasInativas.map((cota) => (
                      <Tr key={cota.id}>
                        <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                          {cota.createdAt
                            ? new Date(cota.createdAt).toLocaleDateString("pt-BR")
                            : "-"}
                        </Td>
                        <Td {...tableCellStyles}>{cota.numeroConsultas ?? 0}</Td>
                        <Td {...tableCellStyles}>{cota.consultasMargem ?? 0}</Td>
                        <Td {...tableCellStyles}>
                          {cota.numeroConsultasSucesso ?? 0}
                        </Td>
                        <Td {...tableCellStyles}>{cota.numeroCcbs ?? 0}</Td>
                        <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                          {cota.dataFimConsumo
                            ? new Date(cota.dataFimConsumo).toLocaleDateString(
                                "pt-BR",
                              )
                            : "-"}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Text textAlign="center" color="gray.500" py={10}>
                Nenhum histórico de consumo encontrado para este corban.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
